/*
  Merqelo theme behaviors. Loaded with `defer` from layout/theme.liquid.
  v0.3:
  - Product card color dots [data-mq-swatch] show that color's photo on hover
  Retired v0.2 behaviors, kept for sections the v0.3 templates no longer use (they only run when
  that markup is on the page, and the motion ones only with Theme settings > Brand motion on):
  - Scroll reveals for .mq-reveal elements
  - The Gather composition [data-mq-art]: laser engraving intro once per session, then a live loop
  - Rotating messages for [data-mq-rotator]
  - Marquee speed normalisation for [data-mq-marquee]
  - Engraving preview for [data-mq-engrave]
  Every feature degrades to static content without JavaScript, and motion is skipped when the
  visitor prefers reduced motion. Sections re-initialise on theme editor section loads.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Reveal on scroll ---------- */

  var revealObserver = null;

  function initReveals(root) {
    var items = (root || document).querySelectorAll('.mq-reveal:not(.is-in)');
    if (!items.length) return;

    if (reduceMotion.matches || designMode || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    items.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------- Gather composition: live laser engraving ---------- */
  /*
    The composition (snippets/mq-gather-art.liquid) is always alive, with nothing to replay.
    - Intro, first homepage view per browser session: dashed cut guides, a vector pass that traces
      each outline, then one raster pass that burns the fills in from top to bottom.
    - Afterwards, and straight away on later views: the finished mark stays whole while the laser
      keeps working over it in a calm loop of finishing passes (white-hot tip, a trail that cools
      behind it, beam-off moves between shapes, an occasional raster shimmer, a short rest).
    One requestAnimationFrame clock drives every layer in the SVG's own units, so the laser, its
    trail and the fills cannot drift apart. The loop pauses while the artwork is off screen.
  */

  var ART_PARTS = ['swoosh', 'pebble', 'stone'];
  var INTRO_SPEED = 720;
  var INTRO_TRAVEL = 0.15;
  var INTRO_RASTER_SPEED = 330;
  var LOOP_SPEED = 240;
  var LOOP_TRAVEL = 0.45;
  var LOOP_RASTER_SPEED = 380;
  var LOOP_REST = 1.6;
  var SETTLE_TIME = 0.7;
  var HOT_LENGTH = 34;
  var TRAIL_LENGTH = 120;
  var GLOW_LENGTH = 260;
  var COOL_TIME = 0.7;
  var SPARK_COUNT = 36;
  var SPARK_COLORS = ['#ff661d', '#ff8a3d', '#ffb347', '#ff7a1f'];
  var SEEN_KEY = 'mq-engraved';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function easeInOut(u) {
    return u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
  }

  function initArt(root) {
    (root || document).querySelectorAll('[data-mq-art]').forEach(function (svg) {
      if (svg.__mqArt) return;
      svg.__mqArt = true;
      createArt(svg);
    });
  }

  function createArt(svg) {
    if (!svg.hasAttribute('data-mq-art-engrave') || reduceMotion.matches || designMode) return;

    var parts = ART_PARTS.map(function (name) {
      var group = svg.querySelector('.mq-gather__part--' + name);
      return {
        guide: group ? group.querySelector('.mq-gather__guide') : null,
        burn: group ? group.querySelector('.mq-gather__burn') : null,
        trail: group ? group.querySelector('.mq-gather__trail') : null,
        hot: group ? group.querySelector('.mq-gather__hot') : null,
        length: 0
      };
    });
    var reveal = svg.querySelector('.mq-gather__reveal');
    var heat = svg.querySelector('.mq-gather__heat');
    var laser = svg.querySelector('.mq-gather__laser');
    var bloom = svg.querySelector('.mq-gather__bloom');
    var core = svg.querySelector('.mq-gather__core');
    var sparkLayer = svg.querySelector('.mq-gather__sparks');
    var complete = parts.every(function (part) {
      return part.guide && part.burn && part.trail && part.hot;
    });
    if (!complete || !reveal || !heat || !laser || !bloom || !core || !sparkLayer) return;

    var boxValues = (svg.getAttribute('data-mq-box') || '0 0 0 0').split(' ').map(parseFloat);
    var box = { x: boxValues[0], y: boxValues[1], w: boxValues[2], h: boxValues[3] };
    var revealY = parseFloat(reveal.getAttribute('y'));
    var revealH = parseFloat(reveal.getAttribute('height'));
    var probe = svg.createSVGPoint ? svg.createSVGPoint() : null;
    var rest = { x: box.x + 14, y: box.y + box.h - 20 };

    parts.forEach(function (part) {
      part.length = part.burn.getTotalLength();
    });

    function pointOn(part, distance) {
      var p = part.burn.getPointAtLength(distance);
      return { x: p.x, y: p.y };
    }

    /* ----- Timelines, in seconds ----- */

    function buildIntro() {
      var steps = [];
      var clock = 0.25;
      parts.forEach(function (part, index) {
        if (index > 0) {
          steps.push({ kind: 'travel', t0: clock, t1: clock + INTRO_TRAVEL, from: pointOn(parts[index - 1], parts[index - 1].length), to: pointOn(part, 0) });
          clock += INTRO_TRAVEL;
        }
        var duration = Math.max(0.45, part.length / INTRO_SPEED);
        steps.push({ kind: 'trace', mode: 'score', t0: clock, t1: clock + duration, part: part });
        clock += duration;
      });
      steps.push({ kind: 'travel', t0: clock, t1: clock + INTRO_TRAVEL, from: pointOn(parts[parts.length - 1], parts[parts.length - 1].length), to: { x: box.x + box.w, y: box.y } });
      clock += INTRO_TRAVEL;
      steps.push({ kind: 'raster', reveal: true, t0: clock, t1: clock + box.h / INTRO_RASTER_SPEED });
      clock += box.h / INTRO_RASTER_SPEED;
      steps.push({ kind: 'settle', t0: clock, t1: clock + SETTLE_TIME, from: { x: box.x + box.w / 2, y: box.y + box.h }, to: rest });
      clock += SETTLE_TIME;
      return { steps: steps, length: clock };
    }

    function buildLoop() {
      var steps = [];
      var clock = 0;
      parts.forEach(function (part, index) {
        if (index > 0) {
          steps.push({ kind: 'travel', t0: clock, t1: clock + LOOP_TRAVEL, from: pointOn(parts[index - 1], parts[index - 1].length), to: pointOn(part, 0) });
          clock += LOOP_TRAVEL;
        }
        var duration = part.length / LOOP_SPEED;
        steps.push({ kind: 'trace', mode: 'pass', t0: clock, t1: clock + duration, part: part });
        clock += duration;
      });
      steps.push({ kind: 'travel', t0: clock, t1: clock + LOOP_TRAVEL, from: pointOn(parts[parts.length - 1], parts[parts.length - 1].length), to: { x: box.x + box.w, y: box.y } });
      clock += LOOP_TRAVEL;
      steps.push({ kind: 'raster', reveal: false, t0: clock, t1: clock + box.h / LOOP_RASTER_SPEED });
      clock += box.h / LOOP_RASTER_SPEED;
      steps.push({ kind: 'travel', t0: clock, t1: clock + LOOP_TRAVEL, from: { x: box.x + box.w / 2, y: box.y + box.h }, to: rest });
      clock += LOOP_TRAVEL;
      var restAt = clock;
      steps.push({ kind: 'rest', t0: clock, t1: clock + LOOP_REST, at: rest });
      clock += LOOP_REST;
      steps.push({ kind: 'travel', t0: clock, t1: clock + LOOP_TRAVEL, from: rest, to: pointOn(parts[0], 0) });
      clock += LOOP_TRAVEL;
      return { steps: steps, length: clock, restAt: restAt };
    }

    var loop = buildLoop();
    var seen = false;
    try {
      seen = !!window.sessionStorage.getItem(SEEN_KEY);
    } catch (error) {
      seen = false;
    }
    var intro = seen ? null : buildIntro();

    /* ----- Sparks: a small reusable pool of streaks ----- */

    var sparks = [];
    var sparkCursor = 0;
    var sparkBudget = 0;
    for (var i = 0; i < SPARK_COUNT; i++) {
      var streak = document.createElementNS(SVG_NS, 'line');
      streak.setAttribute('class', 'mq-gather__spark');
      streak.style.opacity = '0';
      sparkLayer.appendChild(streak);
      sparks.push({ el: streak, life: 1, max: 1, x: 0, y: 0, vx: 0, vy: 0 });
    }

    function emit(x, y, count) {
      for (var n = 0; n < count; n++) {
        var spark = sparks[sparkCursor];
        sparkCursor = (sparkCursor + 1) % sparks.length;
        var angle = Math.random() * Math.PI * 2;
        var speed = 60 + Math.random() * 150;
        spark.x = x;
        spark.y = y;
        spark.vx = Math.cos(angle) * speed;
        spark.vy = Math.sin(angle) * speed - 45;
        spark.life = 0;
        spark.max = 0.14 + Math.random() * 0.32;
        spark.el.style.stroke = SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)];
        spark.el.style.strokeWidth = (0.6 + Math.random() * 0.7).toFixed(2);
      }
    }

    function updateSparks(dt) {
      sparks.forEach(function (spark) {
        if (spark.life >= spark.max) {
          if (spark.el.style.opacity !== '0') spark.el.style.opacity = '0';
          return;
        }
        spark.life += dt;
        spark.vy += 260 * dt;
        spark.vx *= 0.98;
        spark.x += spark.vx * dt;
        spark.y += spark.vy * dt;
        spark.el.setAttribute('x1', spark.x.toFixed(2));
        spark.el.setAttribute('y1', spark.y.toFixed(2));
        spark.el.setAttribute('x2', (spark.x - spark.vx * 0.035).toFixed(2));
        spark.el.setAttribute('y2', (spark.y - spark.vy * 0.035).toFixed(2));
        spark.el.style.opacity = Math.max(0, 1 - spark.life / spark.max).toFixed(3);
      });
    }

    /* ----- Drawing helpers ----- */

    function segment(path, length, drawn, span) {
      path.style.strokeDasharray = span + ' ' + (length + span);
      path.style.strokeDashoffset = String(span - drawn);
    }

    function scored(path, length, drawn) {
      path.style.strokeDasharray = length + ' ' + length;
      path.style.strokeDashoffset = String(length - drawn);
    }

    function setOpacity(el, value) {
      var text = value <= 0.001 ? '0' : value.toFixed(3);
      if (el.style.opacity !== text) el.style.opacity = text;
    }

    function setHead(x, y, firing) {
      var cx = x.toFixed(2);
      var cy = y.toFixed(2);
      core.setAttribute('cx', cx);
      core.setAttribute('cy', cy);
      bloom.setAttribute('cx', cx);
      bloom.setAttribute('cy', cy);
      if (firing) {
        bloom.setAttribute('r', (13 + Math.random() * 5).toFixed(2));
        bloom.style.opacity = (0.7 + Math.random() * 0.3).toFixed(2);
        core.setAttribute('r', '2.3');
        core.style.opacity = '1';
      } else {
        bloom.style.opacity = '0';
        core.setAttribute('r', '1.3');
        core.style.opacity = '0.5';
      }
    }

    function insideJob(x, y) {
      if (!probe) return true;
      probe.x = x;
      probe.y = y;
      try {
        for (var k = 0; k < parts.length; k++) {
          if (parts[k].guide.isPointInFill(probe)) return true;
        }
        return false;
      } catch (error) {
        return true;
      }
    }

    function hideLayers() {
      parts.forEach(function (part) {
        setOpacity(part.hot, 0);
        setOpacity(part.trail, 0);
        setOpacity(part.burn, 0);
      });
    }

    /* ----- Rendering one moment of a timeline ----- */

    function render(timeline, t, dt) {
      var rate = 0;
      var headX = 0;
      var headY = 0;
      var heatOn = false;

      for (var s = 0; s < timeline.steps.length; s++) {
        var step = timeline.steps[s];
        if (t < step.t0) break;
        var u = Math.min(1, (t - step.t0) / (step.t1 - step.t0));
        var active = t < step.t1;
        var part = step.part;

        if (step.kind === 'trace') {
          if (active) {
            var drawn = part.length * easeInOut(u);
            var at = part.burn.getPointAtLength(drawn);
            segment(part.hot, part.length, drawn, HOT_LENGTH);
            setOpacity(part.hot, 1);
            if (step.mode === 'score') {
              scored(part.burn, part.length, drawn);
              setOpacity(part.burn, 0.95);
            } else {
              segment(part.trail, part.length, drawn, TRAIL_LENGTH);
              segment(part.burn, part.length, drawn, GLOW_LENGTH);
              setOpacity(part.trail, 0.85);
              setOpacity(part.burn, 0.3);
            }
            setHead(at.x, at.y, true);
            rate = step.mode === 'score' ? 95 : 30;
            headX = at.x;
            headY = at.y;
          } else if (step.mode === 'score') {
            scored(part.burn, part.length, part.length);
            setOpacity(part.hot, 0);
          } else {
            var age = t - step.t1;
            var cool = Math.max(0, 1 - age / COOL_TIME);
            segment(part.hot, part.length, part.length, HOT_LENGTH);
            segment(part.trail, part.length, part.length, TRAIL_LENGTH);
            segment(part.burn, part.length, part.length, GLOW_LENGTH);
            setOpacity(part.hot, Math.max(0, 1 - age / 0.18));
            setOpacity(part.trail, 0.85 * cool);
            setOpacity(part.burn, 0.3 * cool);
          }
        } else if (step.kind === 'travel' || step.kind === 'settle') {
          if (active) {
            var e = easeInOut(u);
            setHead(step.from.x + (step.to.x - step.from.x) * e, step.from.y + (step.to.y - step.from.y) * e, false);
          }
          if (step.kind === 'settle') {
            parts.forEach(function (p) {
              setOpacity(p.guide, 0.42 * (1 - u));
              setOpacity(p.burn, 0.95 * (1 - u));
            });
          }
        } else if (step.kind === 'rest') {
          if (active) setHead(step.at.x, step.at.y, false);
        } else if (step.kind === 'raster') {
          var y = box.y + box.h * u;
          if (active) {
            if (step.reveal) reveal.setAttribute('height', (y - revealY).toFixed(2));
            heat.setAttribute('transform', 'translate(0 ' + y.toFixed(2) + ')');
            heatOn = true;
            var sweep = Math.abs(((t - step.t0) * 3.4) % 2 - 1);
            var x = box.x + box.w * sweep;
            var firing = insideJob(x, y);
            setHead(x, y, firing);
            if (firing) {
              rate = step.reveal ? 70 : 40;
              headX = x;
              headY = y;
            }
          } else if (step.reveal) {
            reveal.setAttribute('height', String(revealH));
          }
        }
      }

      setOpacity(heat, heatOn ? 1 : 0);

      if (rate) {
        sparkBudget += rate * dt;
        var burst = Math.floor(sparkBudget);
        if (burst > 0) {
          sparkBudget -= burst;
          emit(headX, headY, burst);
        }
      }
      updateSparks(dt);
    }

    /* ----- State and clock ----- */

    var phase = intro ? 'intro' : 'loop';
    var clock = intro ? 0 : loop.restAt + LOOP_REST - 0.6;
    var started = !intro;
    var frameId = null;
    var last = 0;
    var inView = false;

    function prepareIntro() {
      svg.classList.add('is-ready', 'is-engraving');
      reveal.setAttribute('height', '0');
      parts.forEach(function (part) {
        setOpacity(part.guide, 0.42);
        scored(part.burn, part.length, 0);
      });
      hideLayers();
      setOpacity(heat, 0);
      setOpacity(laser, 0);
    }

    function enterLoop() {
      phase = 'loop';
      clock = loop.restAt;
      svg.classList.remove('is-engraving');
      svg.classList.add('is-ready', 'is-live');
      reveal.setAttribute('height', String(revealH));
      parts.forEach(function (part) {
        setOpacity(part.guide, 0);
      });
      hideLayers();
      setOpacity(laser, 1);
      try {
        window.sessionStorage.setItem(SEEN_KEY, '1');
      } catch (error) {
        /* private mode: the intro simply plays again next time */
      }
    }

    function frame(now) {
      if (!inView) {
        frameId = null;
        return;
      }
      var dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      clock += dt;

      if (phase === 'intro') {
        setOpacity(laser, 1);
        render(intro, clock, dt);
        if (clock >= intro.length) enterLoop();
      } else {
        if (clock >= loop.length) {
          clock -= loop.length;
          hideLayers();
        }
        render(loop, clock, dt);
      }
      frameId = requestAnimationFrame(frame);
    }

    function resume() {
      if (frameId) return;
      last = 0;
      frameId = requestAnimationFrame(frame);
    }

    if (intro) {
      prepareIntro();
    } else {
      svg.classList.add('is-ready', 'is-live');
      setOpacity(laser, 1);
    }

    /* Freeze a moment for review from the console: phase 'intro' or 'loop', time in seconds. */
    svg.mqArt = {
      seek: function (which, seconds) {
        var timeline = which === 'intro' && intro ? intro : loop;
        if (timeline === intro) {
          prepareIntro();
        } else {
          svg.classList.remove('is-engraving');
          svg.classList.add('is-ready', 'is-live');
          reveal.setAttribute('height', String(revealH));
          parts.forEach(function (part) { setOpacity(part.guide, 0); });
          hideLayers();
        }
        setOpacity(laser, 1);
        inView = false;
        for (var tt = Math.max(0, seconds - 0.3); tt <= seconds; tt += 1 / 60) render(timeline, tt, 1 / 60);
      },
      introLength: intro ? intro.length : 0,
      loopLength: loop.length
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries.some(function (entry) { return entry.isIntersecting; });
        if (inView && !started) {
          started = true;
          setTimeout(resume, 160);
        } else if (inView && started) {
          resume();
        }
      }, { threshold: 0.2 }).observe(svg);
    } else {
      inView = true;
      started = true;
      resume();
    }
  }

  /* ---------- Rotating messages ---------- */

  function initRotators(root) {
    (root || document).querySelectorAll('[data-mq-rotator]').forEach(function (rotator) {
      if (rotator.__mqRotator) clearInterval(rotator.__mqRotator);
      var items = rotator.querySelectorAll('[data-mq-rotator-item]');
      if (items.length < 2) return;

      var index = 0;
      var delay = parseInt(rotator.getAttribute('data-mq-rotator'), 10) || 5000;
      var paused = false;

      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === 0);
        item.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
      });

      rotator.addEventListener('mouseenter', function () { paused = true; });
      rotator.addEventListener('mouseleave', function () { paused = false; });
      rotator.addEventListener('focusin', function () { paused = true; });
      rotator.addEventListener('focusout', function () { paused = false; });

      rotator.__mqRotator = setInterval(function () {
        if (paused || document.hidden) return;
        items[index].classList.remove('is-active');
        items[index].setAttribute('aria-hidden', 'true');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
        items[index].setAttribute('aria-hidden', 'false');
      }, delay);
    });
  }

  /* ---------- Marquee ---------- */

  function initMarquees(root) {
    (root || document).querySelectorAll('[data-mq-marquee]').forEach(function (marquee) {
      var track = marquee.querySelector('.mq-marquee__group');
      if (!track) return;
      var speed = parseFloat(marquee.getAttribute('data-mq-marquee')) || 60;
      var width = track.getBoundingClientRect().width;
      if (width > 0) {
        marquee.style.setProperty('--mq-marquee-duration', (width / speed).toFixed(2) + 's');
      }
    });
  }

  /* ---------- Engraving preview ---------- */

  function initEngrave(root) {
    (root || document).querySelectorAll('[data-mq-engrave]').forEach(function (widget) {
      if (widget.__mqEngrave) return;
      widget.__mqEngrave = true;

      var input = widget.querySelector('[data-mq-engrave-input]');
      var plate = widget.querySelector('[data-mq-engrave-plate]');
      var output = widget.querySelectorAll('[data-mq-engrave-text]');
      var finishes = widget.querySelectorAll('[data-mq-engrave-finish]');
      var finishName = widget.querySelector('[data-mq-engrave-finish-name]');
      var placeholder = widget.getAttribute('data-placeholder') || '';
      var timer = null;
      if (!plate) return;

      function burn() {
        if (reduceMotion.matches) return;
        plate.classList.remove('is-burning');
        // Force a reflow so the animation restarts on every change.
        void plate.offsetWidth;
        plate.classList.add('is-burning');
      }

      function render() {
        var value = (input && input.value.trim()) || placeholder;
        output.forEach(function (node) { node.textContent = value; });
        var length = value.length;
        plate.style.setProperty('--mq-engrave-scale', length > 18 ? 0.62 : length > 13 ? 0.78 : length > 9 ? 0.9 : 1);
      }

      if (input) {
        input.addEventListener('input', function () {
          render();
          clearTimeout(timer);
          timer = setTimeout(burn, 260);
        });
      }

      finishes.forEach(function (button) {
        button.addEventListener('click', function () {
          finishes.forEach(function (other) {
            var active = other === button;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          plate.setAttribute('data-finish', button.getAttribute('data-mq-engrave-finish'));
          if (finishName) finishName.textContent = button.getAttribute('data-label') || '';
          burn();
        });
      });

      render();

      if ('IntersectionObserver' in window && !reduceMotion.matches) {
        var seen = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              burn();
              seen.disconnect();
            }
          });
        }, { threshold: 0.45 });
        seen.observe(plate);
      }
    });
  }

  /* ---------- Product card color dots ---------- */
  /* Delegated, so cards loaded later (product search results) work without re-binding. */

  function swatchImage(card) {
    return card ? card.querySelector('.mq-pc__img') : null;
  }

  document.addEventListener('mouseover', function (event) {
    var dot = event.target.closest && event.target.closest('[data-mq-swatch]');
    if (!dot) return;
    var card = dot.closest('[data-mq-card]');
    var img = swatchImage(card);
    if (!img) return;
    if (!img.hasAttribute('data-mq-original')) {
      img.setAttribute('data-mq-original', img.currentSrc || img.src);
    }
    img.removeAttribute('srcset');
    img.src = dot.getAttribute('data-mq-swatch');
    card.querySelectorAll('.mq-pc__swatch.is-active').forEach(function (other) {
      other.classList.remove('is-active');
    });
    dot.classList.add('is-active');
  });

  document.addEventListener('mouseout', function (event) {
    var list = event.target.closest && event.target.closest('.mq-pc__swatches');
    if (!list || (event.relatedTarget && list.contains(event.relatedTarget))) return;
    var card = list.closest('[data-mq-card]');
    var img = swatchImage(card);
    if (!img || !img.hasAttribute('data-mq-original')) return;
    img.src = img.getAttribute('data-mq-original');
    card.querySelectorAll('.mq-pc__swatch.is-active').forEach(function (other) {
      other.classList.remove('is-active');
    });
  });

  /* ---------- Boot ---------- */

  function init(root) {
    initReveals(root);
    initArt(root);
    initRotators(root);
    initMarquees(root);
    initEngrave(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  window.addEventListener('load', function () { initMarquees(document); });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
